'use client';

import {
  fetchAiToken,
  getUrlParam,
  TIPTAP_AI_APP_ID,
} from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-collab-utils';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type AiContextValue = {
  aiToken: string | null | undefined;
  hasAi: boolean;
};

export const AiContext = createContext<AiContextValue>({
  hasAi: false,
  aiToken: undefined,
});

export const AiConsumer = AiContext.Consumer;
export const useAi = (): AiContextValue => {
  const context = useContext(AiContext);
  if (!context) {
    throw new Error('useAi must be used within an AiProvider');
  }
  return context;
};

export const useAiToken = () => {
  const [aiToken, setAiToken] = useState<string | null | undefined>(undefined);
  const [hasAi, setHasAi] = useState<boolean>(true);

  useEffect(() => {
    const noAiParam = getUrlParam('noAi');
    const hasCredentials = !!TIPTAP_AI_APP_ID;
    const shouldHaveAi = parseInt(noAiParam || '0') !== 1 && hasCredentials;

    setHasAi(shouldHaveAi);

    // If we shouldn't have AI, immediately set token to null
    if (!shouldHaveAi) {
      setAiToken(null);
    }
  }, []);

  useEffect(() => {
    if (!hasAi) return;

    const getToken = async () => {
      const token = await fetchAiToken();
      setAiToken(token);
    };

    getToken();
  }, [hasAi]);

  return { aiToken, hasAi };
};

export function AiProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const { hasAi, aiToken } = useAiToken();

  const value = useMemo<AiContextValue>(
    () => ({
      hasAi,
      aiToken,
    }),
    [hasAi, aiToken],
  );

  return <AiContext.Provider value={value}>{children}</AiContext.Provider>;
}
